import { cn } from "@babybjorn/ui";
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
	type CSSProperties,
	type PropsWithChildren,
} from "react";
import Button from "../../../components/general/Button";
import * as s from "./ScrollableTabs.module.scss";

function getInheritedBackgroundColor(el: HTMLElement): string {
	// get default style for current browser
	const defaultStyle = getDefaultBackground(); // typically "rgba(0, 0, 0, 0)"

	// get computed color for el
	const backgroundColor = window.getComputedStyle(el).backgroundColor;
	// if we got a real value, return it
	if (backgroundColor !== defaultStyle) {
		return backgroundColor;
	}

	// if we've reached the top parent el without getting an explicit color, return default
	if (!el.parentElement) return defaultStyle;

	// otherwise, recurse and try again on parent element
	return getInheritedBackgroundColor(el.parentElement);
}

function getDefaultBackground() {
	// have to add to the document in order to use getComputedStyle
	const div = document.createElement("div");
	document.head.append(div);
	const bg = window.getComputedStyle(div).backgroundColor;
	div.remove();
	return bg;
}

export type ScrollableTabsRef = {
	shouldScroll: boolean;
};

type ScrollableTabsProps = PropsWithChildren<{
	className?: string;
	onScroll?: () => void;
	ariaLabelScroll: string;
}>;

const ScrollableTabs = forwardRef<ScrollableTabsRef, ScrollableTabsProps>(
	({ children, className, onScroll, ariaLabelScroll }, ref) => {
		const listRef = useRef<HTMLUListElement>(null);

		const [gradientColors, setGradientColors] = useState<CSSProperties>({});
		const [shouldScroll, setShouldScroll] = useState(false);

		useImperativeHandle(
			ref,
			() => ({
				shouldScroll,
			}),
			[shouldScroll],
		);

		const calcTabsWidth = () => {
			if (!listRef.current) return;

			const listEl = listRef.current;

			const totalTabsWidth = [...listEl.childNodes]
				.map((t) => {
					const tab = t as HTMLLIElement;
					return tab.offsetWidth;
				})
				.reduce((partialSum, tabWidth) => partialSum + tabWidth, 0);

			setShouldScroll(
				totalTabsWidth + 45 > (listEl.parentElement?.offsetWidth || 0),
			);
		};

		const getGradientColors = () => {
			if (!listRef.current) return;

			const listEl = listRef.current;
			const color = getInheritedBackgroundColor(listEl); // get the set background color, anywhere in the tree
			const [red, green, blue] = color
				.slice(color.indexOf("(") + 1, color.indexOf(")")) // create a slice that is the contents inbetween the `(` `)`
				.split(",") // create an array with all the values
				.map((val) => val.trim()); // trim excess white space

			const colors = {
				"--tabs-overflow-transparent": `rgba(${red}, ${green}, ${blue}, 0)`,
				"--tabs-overflow-solid": `rgba(${red}, ${green}, ${blue}, 1)`,
			} as CSSProperties;

			setGradientColors(colors);
		};

		useEffect(() => {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
			if (shouldScroll) {
				getGradientColors();
			}
		}, [shouldScroll]);

		useEffect(() => {
			calcTabsWidth();

			window.addEventListener("resize", calcTabsWidth);
			return () => {
				window.removeEventListener("resize", calcTabsWidth);
			};
		}, []);

		const scrollRightClickHandler = () => {
			if (listRef.current) {
				listRef.current.scrollBy({
					top: 0,
					left: 150,
					behavior: "smooth",
				});
			}
		};

		return (
			<div
				className={cn(s.scrollWrapper, className, s.tabsWithArrow, {
					[s.tabsOverflow]: shouldScroll,
				})}
				style={gradientColors}
			>
				<ul
					ref={listRef}
					className={cn(s.tabs, {
						[s.noScroll]: !shouldScroll,
					})}
					onScroll={onScroll}
				>
					{children}
				</ul>
				{shouldScroll && (
					<div className={s.scrollRightButton}>
						<Button
							buttonType="icon"
							icon="arrow-right"
							variant="container"
							aria-label={ariaLabelScroll}
							onClick={scrollRightClickHandler}
						/>
					</div>
				)}
			</div>
		);
	},
);

ScrollableTabs.displayName = "ScrollableTabs";

export default ScrollableTabs;
